import { ArrowLeft, Loader2, ShieldCheck } from "lucide-react";
import { Link as RouterLink } from "react-router-dom";
import { Button } from "../components/nuclear/button";
import "./CheckoutPaddle.module.css";
import useCheckoutPaddleModel from "../models/account/useCheckoutPaddleModel";
import { CHECKOUT_BILLING_TERMS } from "../models/account/checkoutPaddleServices";
import EXTERNAL_LINKS from "../links";

function BillingTermToggle({ billingTerm, handleBillingTermChange }) {
    const termOptions = [
        { value: CHECKOUT_BILLING_TERMS.ANNUAL, label: "Annual" },
        { value: CHECKOUT_BILLING_TERMS.MONTHLY, label: "Monthly" },
    ];

    return (
        <div className={"checkout-paddle-term-toggle"} role={"tablist"} aria-label={"Billing term"}>
            {termOptions.map((option) => (
                <button
                    key={option.value}
                    type={"button"}
                    role={"tab"}
                    aria-selected={billingTerm === option.value ? "true" : "false"}
                    className={"checkout-paddle-term-button"}
                    data-active={billingTerm === option.value ? "true" : "false"}
                    onClick={(event) => {
                        event.stopPropagation();
                        handleBillingTermChange(option.value);
                    }}
                >
                    {option.label}
                </button>
            ))}
        </div>
    );
}

function PlanPrice({ plan }) {
    if (plan.isPriceLoading) {
        return (
            <div className={"checkout-paddle-card-price-loading"} role={"status"} aria-live={"polite"}>
                <Loader2 size={16} className={"checkout-paddle-loader-icon"} aria-hidden={"true"} />
                <span>Loading live pricing</span>
            </div>
        );
    }

    if (!plan.price) {
        return (
            <div className={"checkout-paddle-card-price-block"}>
                <p className={"checkout-paddle-card-price-static"}>Unavailable</p>
                <p className={"checkout-paddle-card-price-caption"}>Paddle pricing could not be loaded right now.</p>
            </div>
        );
    }

    const displayValue = plan.price?.displayValue ?? "";
    const displayValueMatch = typeof displayValue === "string"
        ? displayValue.match(/^([^\d-]*\d[\d,.]*)(.*)$/)
        : null;
    const amountValue = displayValueMatch?.[1] ?? displayValue;
    const suffixValue = displayValueMatch?.[2]?.trim() ?? "";

    return (
        <div className={"checkout-paddle-card-price-block checkout-paddle-card-price-block--inline"}>
            <p className={"checkout-paddle-card-price"}>{amountValue}</p>
            <p className={"checkout-paddle-card-price-caption"}>
                {suffixValue || plan.price?.billingLabel}
            </p>
        </div>
    );
}

function PlanCard({ plan, isSelected, onSelect, onBillingTermChange }) {
    function handleKeyDown(event) {
        if (event.target !== event.currentTarget) {
            return;
        }
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onSelect(plan.key);
        }
    }

    return (
        <div
            className={"checkout-paddle-plan-card"}
            data-selected={isSelected ? "true" : "false"}
            role={"button"}
            tabIndex={0}
            onClick={() => onSelect(plan.key)}
            onKeyDown={handleKeyDown}
            aria-pressed={isSelected}
        >
            <div className={"checkout-paddle-plan-card-header"}>
                <div className={"checkout-paddle-plan-card-copy"}>
                    <h2 className={"checkout-paddle-plan-card-title"}>{plan.title}</h2>
                    <div className={"checkout-paddle-plan-card-toggle-wrap"}>
                        <BillingTermToggle
                            billingTerm={plan.billingTerm}
                            handleBillingTermChange={(nextBillingTerm) => onBillingTermChange(plan.key, nextBillingTerm)}
                        />
                    </div>
                </div>
            </div>

            <PlanPrice plan={plan} />

            <Button
                type={"button"}
                className={"checkout-paddle-plan-select-button"}
                onClick={(event) => {
                    event.stopPropagation();
                    onSelect(plan.key);
                }}
            >
                {isSelected ? "Selected" : "Select"}
            </Button>
        </div>
    );
}

function CheckoutSummary({ selectedPlan, selectedPrice, isPaddleLoading, canCheckout, launchCheckout, isPricingReady }) {
    if (!selectedPlan) {
        return null;
    }

    return (
        <div className={"checkout-paddle-summary-card"}>
            <div className={"checkout-paddle-summary-total checkout-paddle-summary-total--row"}>
                <span className={"checkout-paddle-summary-total-label"}>Selected plan</span>
                <strong className={"checkout-paddle-summary-total-value"}>
                    {selectedPrice?.billingValue ?? (isPaddleLoading ? "Loading..." : "Unavailable")}
                </strong>
            </div>

            <Button
                type={"button"}
                fullWidth={true}
                onClick={launchCheckout}
                disabled={!canCheckout || !isPricingReady}
            >
                COMPLETE CHECKOUT
            </Button>

            <div className={"checkout-paddle-security-note"}>
                <div className={"checkout-paddle-security-note-header"}>
                    <ShieldCheck size={18} aria-hidden={"true"} />
                    <span className={"checkout-paddle-security-note-title"}>Secure checkout</span>
                </div>
                <p className={"checkout-paddle-security-note-copy"}>
                    Your subscription will auto-renew. You can cancel or change plans at any time in your
                    dashboard settings.
                </p>
            </div>
        </div>
    );
}

function ProductSelection({
    plans,
    selectedPlan,
    selectedPrice,
    isPaddleLoading,
    canCheckout,
    isPricingReady,
    handlePlanChange,
    handleBillingTermChange,
    launchCheckout,
}) {
    return (
        <div className={"checkout-paddle-content"}>
            <div className={"checkout-paddle-topbar"}>
                <Button
                    type={"button"}
                    variant={"ghost"}
                    className={"checkout-paddle-back-button"}
                    onClick={() => window.history.back()}
                >
                    <ArrowLeft size={16} aria-hidden={"true"} />
                    <span>Back</span>
                </Button>
            </div>

            <header className={"checkout-paddle-hero"}>
                <h1 className={"checkout-paddle-hero-title"}>Upgrade to Animotive Pro.</h1>
                <p className={"checkout-paddle-hero-copy"}>
                    Use Animotive in your commercial projects with Pro-level storage (20 GB) and service.
                </p>
                <p className={"checkout-paddle-hero-copy"}>
                    Discounted rate available for startups and small studios.
                </p>
            </header>

            <h2 className={"checkout-paddle-selection-heading"}>
                Select your billing plan based on your annual revenue:
            </h2>

            <div className={"checkout-paddle-main-grid"}>
                <div className={"checkout-paddle-selection-column"}>
                    <div className={"checkout-paddle-plan-grid"} role={"list"} aria-label={"Checkout plans"}>
                        {plans.map((plan) => (
                            <PlanCard
                                key={plan.key}
                                plan={plan}
                                isSelected={selectedPlan?.key === plan.key}
                                onSelect={handlePlanChange}
                                onBillingTermChange={handleBillingTermChange}
                            />
                        ))}
                    </div>

                    <div className={"checkout-paddle-contact-strap"}>
                        <p className={"checkout-paddle-contact-strap-copy"}>
                            For <strong>education</strong> and <strong>enterprise</strong> plans, please{" "}
                            <a href={EXTERNAL_LINKS.contact.supportEmailHref}>contact us</a>
                        </p>
                    </div>
                </div>

                <CheckoutSummary
                    selectedPlan={selectedPlan}
                    selectedPrice={selectedPrice}
                    isPaddleLoading={isPaddleLoading}
                    canCheckout={canCheckout}
                    launchCheckout={launchCheckout}
                    isPricingReady={isPricingReady}
                />
            </div>
        </div>
    );
}

function CheckoutStateCard({ title, message }) {
    return (
        <div className={"checkout-paddle-summary-card checkout-paddle-summary-card--status"}>
            <p className={"checkout-paddle-summary-eyebrow"}>{title}</p>
            <p className={"checkout-paddle-summary-copy"}>{message}</p>
            <Button asChild={true}>
                <RouterLink to={"/"}>Go back to home</RouterLink>
            </Button>
        </div>
    );
}

export default function CheckoutPaddle() {
    const model = useCheckoutPaddleModel();
    const {
        plans,
        selectedPlan,
        selectedPrice,
        isPaddleLoading,
        isPricingReady,
        canCheckout,
        showUpgradeSuccess,
        showAlreadyPro,
        showManagedPlan,
        showPricingSelection,
        handlePlanChange,
        handleBillingTermChange,
        launchCheckout,
    } = model;

    function MainCheckout() {
        if (showUpgradeSuccess) {
            return (
                <CheckoutStateCard
                    title={"Subscription updated"}
                    message={"Thanks for updating your Animotive subscription."}
                />
            );
        }

        if (showManagedPlan) {
            return (
                <CheckoutStateCard
                    title={"Managed plan"}
                    message={"Your subscription is managed by your organisation. Contact your seat manager for changes."}
                />
            );
        }

        if (showAlreadyPro) {
            return (
                <CheckoutStateCard
                    title={"Paid plan active"}
                    message={"Your account already has an active paid plan. You can manage billing from account settings."}
                />
            );
        }

        if (showPricingSelection) {
            return (
                <ProductSelection
                    plans={plans}
                    selectedPlan={selectedPlan}
                    selectedPrice={selectedPrice}
                    isPaddleLoading={isPaddleLoading}
                    canCheckout={canCheckout}
                    isPricingReady={isPricingReady}
                    handlePlanChange={handlePlanChange}
                    handleBillingTermChange={handleBillingTermChange}
                    launchCheckout={launchCheckout}
                />
            );
        }

        return null;
    }

    return (
        <main className={"checkout-paddle-shell"}>
            <MainCheckout />
        </main>
    );
}
